import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Activity, Loader2, RefreshCw, SearchCheck } from "lucide-react";
import api from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Empty52 } from "@/components/v52/Bits";
import { errText, useImportCall } from "@/components/v52/ImportParts";
import { CountTiles, LightCard, Readiness, RunPanel, downloadRunReport } from "@/components/v52/OpsParts";
import { BackupsPanel, HeldPanel, JournalPanel, OpsDialog, ResultsPanel } from "@/components/v52/OpsLists";
import { SupplementPanel } from "@/components/v52/SupplementPanel";
import { useT52 } from "@/i18n/v52";

const ACTIVE = ["QUEUED", "BACKUP", "RESTORE_CHECK", "PREFLIGHT", "IMPORTING", "VERIFYING"];

export default function ImportBetrieb() {
  const { t52, tOr } = useT52();
  const [ov, setOv] = useState(null);
  const [dlg, setDlg] = useState(null);
  const [pending, setPending] = useState(false);
  const load = useCallback(() => api.get("/import-ops/overview").then((r) => setOv(r.data)).catch(() => setOv(false)), []);
  useEffect(() => { load(); }, [load]);
  const active = ACTIVE.includes(ov?.run?.state);
  const appRunning = ov?.app_backup?.status === "running";
  useEffect(() => {
    if (!active && !appRunning) return undefined;
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, [active, appRunning, load]);
  const call = useImportCall(load, setPending);

  if (ov === null) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-fundio-gold" /></div>;
  const header = (actions) => <PageHeader title={t52("ops_title")} subtitle={t52("ops_lead")}>{actions}</PageHeader>;
  if (ov === false) return <div data-testid="ops-page">{header()}<p data-testid="ops-load-error" className="f52-imp-warn">{t52("ops_load_err")}</p></div>;
  if (!ov.enabled) return <div data-testid="ops-page">{header()}<Empty52 icon={Activity} title={t52("ops_off_t")} text={t52("ops_off_d")} testid="ops-disabled" /></div>;

  const run = ov.run;
  const busy = pending;
  const actions = (
    <div className="flex flex-wrap gap-2">
      <button type="button" onClick={load} data-testid="ops-refresh" className="f52-btn f52-btn-secondary"><RefreshCw aria-hidden="true" />{t52("ops_refresh")}</button>
      <button type="button" disabled={busy} onClick={() => call(() => api.post("/import-ops/health-check"), "ops_check_ok")} data-testid="ops-health-check" className="f52-btn f52-btn-secondary"><SearchCheck aria-hidden="true" />{t52("ops_check")}</button>
    </div>
  );
  const report = async (rid = run.id) => {
    try { await downloadRunReport(rid); toast.success(t52("ops_report_ok")); } catch (e) { toast.error(errText(t52, tOr, e)); }
  };
  const submit = async (v) => {
    const url = { start: "/import-ops/runs", supplement: "/import-ops/supplement/runs" }[dlg];
    const ok = url
      ? await call(() => api.post(url, v), dlg === "start" ? "ops_started" : "ops_sp_started")
      : await call(() => api.post(`/import-ops/runs/${run.id}/resume`, { target_confirm: v.target_confirm, totp_code: v.totp_code }), "ops_resumed");
    if (ok) setDlg(null);
  };
  return (
    <div data-testid="ops-page" className="f52-imp">
      {header(actions)}
      <LightCard light={ov.light} />
      <CountTiles health={ov.health} lastOk={ov.last_ok} />
      <Readiness r={ov.health?.readiness} />
      <RunPanel run={run} busy={busy} onStart={() => setDlg("start")} onResume={() => setDlg("resume")}
        onStop={() => call(() => api.post(`/import-ops/runs/${run.id}/stop`), "ops_stop_ok")} onReport={() => report()} />
      <SupplementPanel info={ov.supplement} busy={busy} canStart={!active} onStart={() => setDlg("supplement")} />
      <BackupsPanel backups={ov.backups} isProd={ov.environment === "production"} busy={busy} appJob={ov.app_backup}
        onCreate={() => call(() => api.post("/import-ops/backups"), "ops_b_created")}
        onAppBackup={() => call(() => api.post("/import-ops/app-backup"), "ops_ab_started")}
        onAppReconcile={() => call(() => api.post("/import-ops/app-backup/reconcile"), "ops_ab_reconciled_ok")}
        onRestore={(source) => call(async () => {
          const r = await api.post("/import-ops/backups/restore-check", { source });
          if (!r.data.ok) throw Object.assign(new Error("rc"), { response: { data: { detail: { code: r.data.error_code || "restore_failed", msg: (r.data.errors || []).join("; ") } } } });
        }, "ops_b_rc_ok")} />
      <HeldPanel held={run?.held} />
      <ResultsPanel results={ov.results} base={ov.results_base} current={ov.results_current} onBaseReport={() => report(ov.results_base.run_id)} />
      <JournalPanel journal={ov.journal} />
      {dlg && <OpsDialog mode={dlg} ov={ov} onCancel={() => setDlg(null)} onSubmit={submit} />}
    </div>
  );
}
